/**
 * Export Service
 * Provides utilities for exporting data in CSV, Excel, and PDF formats
 * Following best practices for file generation and download
 */

interface ExportOptions {
  filename: string;
  data: any[];
  columns?: string[];
  title?: string;
}

/**
 * Convert array of objects to CSV string
 */
function convertToCSV(data: any[], columns?: string[]): string {
  if (!data || data.length === 0) return "";

  // Get headers from first object or use provided columns
  const headers = columns || Object.keys(data[0]);

  // Create CSV header row
  const csvHeaders = headers.join(",");

  // Create CSV data rows
  const csvRows = data.map((row) => {
    return headers
      .map((header) => {
        const value = row[header];
        // Handle values that contain commas, quotes, or newlines
        if (value === null || value === undefined) return "";
        const stringValue = String(value);
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
      .join(",");
  });

  return [csvHeaders, ...csvRows].join("\n");
}

/**
 * Export data as CSV file
 */
export async function exportToCSV(options: ExportOptions): Promise<void> {
  const { filename, data, columns } = options;

  try {
    const csvContent = convertToCSV(data, columns);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    downloadFile(blob, `${filename}.csv`);
  } catch (error) {
    console.error("CSV export failed:", error);
    throw new Error("Failed to export CSV file");
  }
}

/**
 * Export data as Excel file (using CSV format for simplicity)
 * For production, consider using libraries like xlsx or exceljs for true .xlsx format
 */
export async function exportToExcel(options: ExportOptions): Promise<void> {
  const { filename, data, columns } = options;

  try {
    // For now, we'll create a CSV that Excel can open
    // In production, use a library like 'xlsx' for true Excel format
    const csvContent = convertToCSV(data, columns);
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    downloadFile(blob, `${filename}.xls`);
  } catch (error) {
    console.error("Excel export failed:", error);
    throw new Error("Failed to export Excel file");
  }
}

/**
 * Export data as PDF file
 * Uses browser's print functionality for PDF generation
 */
export async function exportToPDF(options: ExportOptions): Promise<void> {
  const { filename, data, columns, title } = options;

  try {
    // Create a temporary HTML document for PDF generation
    const headers = columns || Object.keys(data[0] || {});

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${title || filename}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
          }
          h1 {
            color: #333;
            font-size: 24px;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #f5f5f5;
            padding: 12px;
            text-align: left;
            border: 1px solid #ddd;
            font-weight: 600;
          }
          td {
            padding: 10px;
            border: 1px solid #ddd;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .footer {
            margin-top: 20px;
            font-size: 12px;
            color: #666;
            text-align: center;
          }
        </style>
      </head>
      <body>
        ${title ? `<h1>${title}</h1>` : ""}
        <table>
          <thead>
            <tr>
              ${headers.map((h) => `<th>${formatHeader(h)}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>
                ${headers.map((h) => `<td>${formatValue(row[h])}</td>`).join("")}
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        <div class="footer">
          Generated on ${new Date().toLocaleString()} | DerraCare Admin Portal
        </div>
      </body>
      </html>
    `;

    // Open in new window and trigger print
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load before printing
      printWindow.onload = () => {
        printWindow.print();
        // Close window after print dialog is closed (give user time to print)
        setTimeout(() => {
          printWindow.close();
        }, 100);
      };
    } else {
      throw new Error("Failed to open print window. Please allow popups.");
    }
  } catch (error) {
    console.error("PDF export failed:", error);
    throw new Error("Failed to export PDF file");
  }
}

/**
 * Download a blob as a file
 */
function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Format header name for display
 */
function formatHeader(header: string): string {
  return header.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Format value for display
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Generic export function that supports multiple formats
 */
export async function exportData(
  format: "csv" | "excel" | "pdf",
  options: ExportOptions,
): Promise<void> {
  switch (format) {
    case "csv":
      return exportToCSV(options);
    case "excel":
      return exportToExcel(options);
    case "pdf":
      return exportToPDF(options);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}
